/**
 * Context for sharing the active payer name within the single-order layout.
 * @module payerNameContextFromSingleOrderLayout
 */

import { createContext, useContext, useState } from "react";

/** Shape of the payer-name context value. */
interface PayerNameContextFromSingleOrderLayoutProps {
  /** Currently selected payer name. */
  payerName: string;
  /** Updates the payer name stored in context. */
  setPayerName: (payerName: string) => void;
}

/**
 * React context for the payer name within the single-order layout.
 * Defaults to an empty payer name and a no-op setter.
 */
export const PayerNameContextFromSingleOrderLayout =
  createContext<PayerNameContextFromSingleOrderLayoutProps>({
    payerName: "",
    setPayerName: () => {},
  });

/**
 * Accesses the payer name context. Must be used inside
 * {@link PayerNameContextFromSingleOrderLayoutProvider}.
 *
 * @returns The current payer name and its setter
 * @throws If used outside of the provider
 */
export const usePayerNameContextFromSingleOrderLayout = () => {
  const context = useContext(PayerNameContextFromSingleOrderLayout);
  if (!context) {
    throw new Error(
      "usePayerNameContextFromSingleOrderLayout must be used within a PayerNameContextFromSingleOrderLayoutProvider",
    );
  }
  return context;
};

/**
 * Provider that stores and distributes the payer name to descendant components
 * within the single-order layout.
 *
 * @param props.children - Child components that need access to the payer name
 */
export const PayerNameContextFromSingleOrderLayoutProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [payerName, setPayerName] = useState<string>("");
  return (
    <PayerNameContextFromSingleOrderLayout.Provider
      value={{ payerName: payerName, setPayerName: setPayerName }}
    >
      {children}
    </PayerNameContextFromSingleOrderLayout.Provider>
  );
};
